import { Button } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import {
  DEFAULT_DATE_FORMAT,
  Kyb,
  CheckCircleOutline,
  CloseCircleOutline,
  EyeOutline,
} from '@aslanpay/portal-common-lib';
import { KybActions } from 'app/components/UI/Table/Table.types';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';
import { format, parseISO } from 'date-fns';

export type KybTableVariant = 'approved' | 'pending' | 'declined';

enum PendingKybField {
  companyName = 'Company name',
  companyNumber = 'Company no.',
}

enum ApprovedKybField {
  companyName = 'Company name',
  approvedByName = 'Approved by',
  approvedAt = 'Approved on',
}

enum DeclinedKybField {
  companyName = 'Company name',
  declinedByName = 'Declined by',
  declinedAt = 'Declined on',
}

function getPendingKybColumns(columnHelper: ReturnType<typeof createColumnHelper<Kyb>>) {
  const fields = Object.keys(PendingKybField) as Array<keyof Kyb>;

  return fields.map((field) => {
    return columnHelper.accessor(field as keyof Kyb, {
      cell: (info) => {
        switch (field as keyof typeof PendingKybField) {
          case 'companyName':
            return info.getValue();
          case 'companyNumber':
            return info.getValue() && <TextWithClipboard value={info.getValue() as string} />;
          default:
            return info.getValue();
        }
      },
      header: PendingKybField[field as keyof typeof PendingKybField],
      meta: {
        getCellContext: () => ({
          style: { textAlign: 'left' },
        }),
      },
    });
  });
}

function getApprovedKybColumns(columnHelper: ReturnType<typeof createColumnHelper<Kyb>>) {
  const fields = Object.keys(ApprovedKybField) as Array<keyof Kyb>;

  return fields.map((field) => {
    return columnHelper.accessor(field as keyof Kyb, {
      cell: (info) => {
        switch (field as keyof typeof ApprovedKybField) {
          case 'companyName':
            return info.getValue();
          case 'approvedByName':
            return info.getValue();
          case 'approvedAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          default:
            return info.getValue();
        }
      },
      header: ApprovedKybField[field as keyof typeof ApprovedKybField],
      meta: {
        getCellContext: () => ({
          style: { textAlign: 'left' },
        }),
      },
    });
  });
}

function getDeclinedKybColumns(columnHelper: ReturnType<typeof createColumnHelper<Kyb>>) {
  const fields = Object.keys(DeclinedKybField) as Array<keyof Kyb>;

  return fields.map((field) => {
    return columnHelper.accessor(field as keyof Kyb, {
      cell: (info) => {
        switch (field as keyof typeof DeclinedKybField) {
          case 'companyName':
            return info.getValue();
          case 'declinedByName':
            return info.getValue();
          case 'declinedAt':
            return format(parseISO(info.getValue() as string), DEFAULT_DATE_FORMAT);
          default:
            return info.getValue();
        }
      },
      header: DeclinedKybField[field as keyof typeof DeclinedKybField],
      meta: {
        getCellContext: () => ({
          style: { textAlign: 'left' },
        }),
      },
    });
  });
}

function getKybColumns(variant: KybTableVariant, kybActions?: KybActions) {
  const columnHelper = createColumnHelper<Kyb>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let columns: ColumnDef<Kyb, any>[];

  if (variant === 'pending') {
    // Base data columns first
    columns = getPendingKybColumns(columnHelper);
  } else if (variant === 'approved') {
    columns = getApprovedKybColumns(columnHelper);
  } else {
    columns = getDeclinedKybColumns(columnHelper);
  }

  // Spacer column to consume remaining horizontal space and push columns to the left and action buttons to the right
  const spacerWidth = variant === 'pending' ? '20%' : '40%';
  columns.push(
    columnHelper.display({
      id: 'spacer',
      meta: {
        getCellContext: () => ({
          style: { width: spacerWidth, padding: 0 },
        }),
        getHeaderContext: () => ({
          style: { width: spacerWidth, padding: 0 },
        }),
      },
      cell: () => null,
      header: () => null,
    })
  );

  if (variant === 'pending') {
    columns.push(
      columnHelper.display({
        id: 'view',
        meta: {
          getCellContext: () => ({
            style: { textAlign: 'center', width: '100px' },
          }),
          getHeaderContext: () => ({
            style: { textAlign: 'center', width: '100px' },
          }),
        },
        cell: ({ row }) => (
          <Button
            variant="link"
            size="sm"
            color="brand.darkPurple"
            leftIcon={<EyeOutline boxSize="20px" />}
            onClick={() => kybActions?.view(row.original as Kyb)}
          >
            View
          </Button>
        ),
      })
    );

    columns.push(
      columnHelper.display({
        id: 'decline',
        meta: {
          getCellContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
          getHeaderContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
        },
        cell: ({ row }) => (
          <Button
            variant="link"
            size="sm"
            color="red"
            leftIcon={<CloseCircleOutline boxSize="20px" />}
            onClick={() => kybActions?.decline(row.original as Kyb)}
          >
            Decline
          </Button>
        ),
      })
    );

    columns.push(
      columnHelper.display({
        id: 'approve',
        meta: {
          getCellContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
          getHeaderContext: () => ({
            style: { textAlign: 'center', width: '120px' },
          }),
        },
        cell: ({ row }) => (
          <Button
            variant="link"
            size="sm"
            color="brand.purple"
            leftIcon={<CheckCircleOutline boxSize="20px" />}
            onClick={() => kybActions?.approve(row.original as Kyb)}
          >
            Approve
          </Button>
        ),
      })
    );
  }

  return columns;
}

export default getKybColumns;
